import { useMediaQuery, useTheme } from '@mui/material';
import { isEmpty } from 'lodash';
import React from 'react';
import { shallowEqual } from 'react-redux';
import { makeStyles } from 'tss-react/mui';

import {
  APP_SETUP_PAGE,
  BillingSettings,
  ModuleSettingsItem,
} from 'src/constants';
import { AppSettingsModalContext } from 'src/features/app-settings-modal/AppSettingsModalContext';
import history from 'src/history';
import { useDefaultCurrency } from 'src/hooks/useDefaultCurrency';
import { useAppDispatch, useAppSelector } from 'src/hooks/useStore';
import { Loader } from 'src/legacy/components/Loading';
import { Currencies } from 'src/services/api/invoicesApi';
import { RootState } from 'src/store';
import { updateBillingSettings } from 'src/store/settings/actions';

import { SetupModulePageDescription } from '../AppSetupPage/SetupApp';
import SetupAppBreadCrumbs from '../AppSetupPage/SetupApp/SetupAppBreadCrumbs';
import { useEditingApp } from '../AppSetupPage/SetupApp/useEditingApp';
import { useAppSetupPaths } from '../AppSetupPage/useAppSetupPaths';
import { ModuleContainer } from '../ModuleContainer';
import { SettingsPageForm } from '../SettingsPageForm';

import { useBillingSettingsForm } from './useBankSettingsForm';

export const initialDefaultsFormValue: BillingSettings = {
  allowPaymentViaCC: true,
  allowPaymentViaACH: true,
  absorbTransactionFees: false,
  absorbACHFees: false,
  absorbCreditCardFee: false,
  defaultMemo: '',
};

const useStyles = makeStyles()(() => ({
  loadingWrapper: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
}));

export const BankingDefaultsSettings: React.FC = () => {
  const { folderId: folderIdParam, addAppPagePath } = useAppSetupPaths();
  // added to the bottom of moduleSettings (not placed in a folder)
  const folderId = folderIdParam;
  const { classes } = useStyles();
  const dispatch = useAppDispatch();
  const modalCtx = React.useContext(AppSettingsModalContext);
  const billingModule = useEditingApp();
  const { billingSettings, isSettingLoaded } = useAppSelector(
    (state: RootState) => ({
      billingSettings: state.settings.billingSettings,
      isSettingLoaded: state.settings.loaded,
    }),
    shallowEqual,
  );

  const defaultCurrency = useDefaultCurrency();
  const billingSettingsWithCurrencyAdjustment = {
    ...billingSettings,
    allowPaymentViaACH:
      // For NON-USD portals, we do not allow ACH payments so toggle needs to be disabled by default
      defaultCurrency.toUpperCase() == Currencies.USD
        ? billingSettings?.allowPaymentViaACH
        : false,
  };

  // settings specific to billing module
  const moduleSettingValues = {
    label: billingModule?.label || '',
    icon: billingModule?.icon || '',
  };

  const handleSave = async (values: BillingSettings & ModuleSettingsItem) => {
    await dispatch(
      updateBillingSettings({
        billingSettings: values,
        currentBillingModule: billingModule,
        folderId,
      }),
    );

    if (!modalCtx) {
      history.push(APP_SETUP_PAGE.path);
    }
  };

  const handleCancel = () => {
    if (modalCtx) {
      modalCtx.onClose();
    } else {
      history.push(addAppPagePath);
    }
  };

  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <ModuleContainer description={<SetupModulePageDescription />}>
      {isSettingLoaded ? (
        <>
          {/*
            TODO: Update this to be a strongly typed form.
            These were any typed before we introduced typesafe forms so
            this isn't a type regression, but it'd be nice to update it.
          */}
          <SettingsPageForm<any>
            BreadcrumbsComponent={<SetupAppBreadCrumbs />}
            initialFormValue={
              !isEmpty(billingSettings)
                ? {
                    ...billingSettingsWithCurrencyAdjustment,
                    ...moduleSettingValues,
                  }
                : { ...initialDefaultsFormValue, ...moduleSettingValues }
            }
            useFormHook={useBillingSettingsForm}
            handleSave={handleSave}
            actionLabel={billingModule?.disabled ? 'Enable' : 'Save'}
            shouldShowPageAction={billingModule?.disabled}
            handleCancel={billingModule?.disabled ? handleCancel : undefined}
            showBackButton={isSm}
            onBackButtonClick={modalCtx?.onClose ?? (() => history.goBack())}
          />
        </>
      ) : (
        <div className={classes.loadingWrapper}>
          <Loader />
        </div>
      )}
    </ModuleContainer>
  );
};
